// Boomerang AI — App Version & Changelog
// Bump APP_VERSION on each release and add a changelog entry at the top.

export const APP_VERSION = "1.9.139";

export const CHANGELOG = [
  {
    version: "1.9.139",
    date: "2026-11-02",
    notes: [
      "IMPROVED (File Upload → Tables): your tables are now one compact grid. Click + next to a table "
      + "(or the row) to see its fields underneath, with columns for type, original column name, "
      + "fields, files and last upload. Expand all / Collapse all buttons and a count of what's shown.",
      "NEW (File Upload → Tables): a Relationships view lists fields that have the same name in two or "
      + "more tables (for example campaign_id). Boomerang uses these to relate tables when you ask "
      + "questions; they are matches by name, not enforced links. Every shared field is shown by "
      + "default; a \"Show only likely join keys\" toggle (off by default) hides dates and metrics "
      + "like spend or clicks that every table has, so keys like campaign_id and campaign_name stand "
      + "out; shared metrics are tagged \"metric\". In the grid, \"in 3 tables\" next to a field "
      + "takes you there.",
    ],
  },
  {
    version: "1.9.138",
    date: "2026-11-02",
    notes: [
      "NEW (File Upload → Tables): a summary at the top (for example \"5 tables · 87 fields · 23 "
      + "files\", plus how many dates, numbers, currency, percent and text fields), each field shown "
      + "as your original column name → the clean name we use (for example \"Amount spent (USD) → "
      + "amount_spent_usd\"), and a search box that finds fields by either name, opens the tables "
      + "that match and highlights the match.",
    ],
  },
  {
    version: "1.9.137",
    date: "2026-11-02",
    notes: [
      "NEW (File Upload): a Tables tab shows each data set as a table you can expand to see its "
      + "fields, each field's type (Number, Date, Currency, Percent or Text) and the original "
      + "column name from your file, plus how many files it has. Turned on per account.",
    ],
  },
  {
    version: "1.9.136",
    date: "2026-11-01",
    notes: [
      "FIXED (Template import → Summary Tables and charts): when several of your data sources "
      + "share column names (for example \"Purchases\" in both Meta and Snapchat), each table now "
      + "reads the ONE source it really comes from. Before, a Snapchat table could show Meta "
      + "numbers or empty cells.",
      "NEW: every table and chart says which data it uses (for example \"Source: Snapchat Daily "
      + "Campaign Performance (matched 'Data Snapchat', 20/20 columns)\"), in the import and on "
      + "the dashboard.",
      "NEW: if we can't tell which of your data sources a table uses, the import asks \"Which data "
      + "does this table use?\" with the options. We never guess.",
      "NEW (File Upload): each data set shows the platform it comes from (Meta, Snapchat, TikTok, "
      + "Reddit, Google Ads, DV360 or Other), marked \"(detected)\" until you confirm it. You can "
      + "change it from the file list; the platform also appears in the upload message and the "
      + "cleaning report.",
      "IMPROVED: the Excel check now separates values that differ from values we couldn't "
      + "calculate, and says why for each group (for example a column your data doesn't have). "
      + "A table shows \"Needs your review before publishing\" until that check is done. On the "
      + "dashboard, empty \"—\" cells now come with a visible note explaining why.",
      "Tables added before this update fix their source automatically where they can; if one "
      + "can't be confirmed, the dashboard says so. Re-import your template (Upload My Template) "
      + "to choose.",
      "A chart built from one platform's pivot (for example Snapchat) never shows another "
      + "platform's numbers: until that data is uploaded it says which data to upload, then it "
      + "fills in by itself.",
      "If a chart's data could come from more than one of your data sets, it says which one it "
      + "uses and which others fit, in the import and on the dashboard. If it's wrong, remove "
      + "that chart in the import; choosing a chart's source is coming soon.",
      "Re-importing your template keeps your earlier choice of data while it still fits, and the "
      + "\"Which data does this table use?\" choice stays so you can change it.",
    ],
  },
  {
    version: "1.9.135",
    date: "2026-10-31",
    notes: [
      "FIXED (Template import → Summary Tables and charts): numbers now follow your Excel "
      + "pivot's filters (for example one week of dates, or only some campaigns). Before, a table "
      + "showed the right rows but counted all of your data.",
      "NEW: each table shows the filters it came with from your Excel as small chips above the "
      + "table (for example \"Day: Sep 28 – Oct 4, 2026\"). A filter we can't apply yet is shown "
      + "too, with the reason, so nothing is hidden.",
      "NEW: when a table's numbers differ from your Excel, the import shows which cells differ "
      + "and why, and asks you to confirm before publishing. Your confirmation is noted on the "
      + "dashboard table.",
      "Tables added before this update: re-import the same Excel (Dashboard → Upload My "
      + "Template) to pick up the filters, then review and Publish.",
      "If the tables you publish are too large to save together in one project, the import now "
      + "names them and tells you what to do, shown once instead of an error.",
    ],
  },
  {
    version: "1.9.134",
    date: "2026-10-30",
    notes: [
      "FIXED (Template import → Add Summary Tables): PivotTables with an Excel \"(blank)\" row or "
      + "with dates as rows (e.g. daily Meta/Snapchat/TikTok/Reddit pivots) no longer get stuck on "
      + "\"things need a quick explanation\" — they're ready to publish. The \"(blank)\" row is "
      + "shown empty, with a short note saying why, never hidden.",
      "IMPROVED: when a table still needs attention, each item now says exactly where and why — "
      + "the sheet and cell (e.g. META TABLAS DINAMICAS!C42), its row and column, the value Excel "
      + "shows, and what to do next. Identical problems are grouped into one item (\"10 cells in "
      + "the (blank) row…\"), and \"Explain\" is only offered where explaining can actually fix it.",
    ],
  },
  {
    version: "1.9.133",
    date: "2026-09-22",
    notes: [
      "NEW (Analyze): you can now turn Auto-Insights on or off per project. Use the "
      + "\"✨ Auto-Insights On/Off\" button in the board toolbar to pause the auto-generated "
      + "dashboard — handy if you only want your pinned charts, a published template, or to ask "
      + "questions without waiting for the board to build. Turning it off generates nothing (so "
      + "switching to that project is instant) and NEVER removes your pinned charts or published "
      + "template — those stay exactly as they are. Turn it back on any time to rebuild the board "
      + "from your current data.",
    ],
  },
  {
    version: "1.9.132",
    date: "2026-09-22",
    notes: [
      "IMPROVED (Analyze): the \"Ask a question about your data\" box is now a taller, multi-line "
      + "field (about double the previous height) with its own scrollbar, so you can see a long "
      + "question across several lines instead of a single cramped line. Press Enter to send as "
      + "before, or Shift+Enter to add a new line; drag the bottom edge to make it taller.",
    ],
  },
  {
    version: "1.9.131",
    date: "2026-10-22",
    notes: [
      "FIXED (Template import): you can now import a template that has only PivotTables / summary "
      + "tables and no charts. Before, such a file was rejected with \"No extractable charts "
      + "found\" and you could never reach the step to pin its tables. Now the import succeeds, "
      + "shows a friendly \"No charts found in this file — that's okay\" notice, and takes you "
      + "straight to the Add Summary Tables step where your detected PivotTables and sheets are "
      + "ready to pin and publish. Templates that do have charts are unchanged.",
    ],
  },
  {
    version: "1.9.130",
    date: "2026-10-16",
    notes: [
      "FIXED (File Upload): opening a file's cleaning report right after an upload could show "
      + "\"No processing metrics available… Use Reprocess\" even though the report existed — a "
      + "read-timing miss, not a missing report. The report now reliably loads (the backend "
      + "lookup is page-safe), and when it isn't retrieved on the first try the panel shows "
      + "\"Report is being generated…\" with a Retry link and a one-shot auto-retry, instead of "
      + "the misleading Reprocess message. The Reprocess copy now appears only when a report "
      + "genuinely isn't there after a retry.",
    ],
  },
  {
    version: "1.9.129",
    date: "2026-10-16",
    notes: [
      "FIXED (Analyze tables): very small rates and large counts shown in scientific notation "
      + "(e.g. a 0.09% CTR arriving as \"8.75...E-4\", or impressions as \"2.17E7\") now format "
      + "correctly in the Source Records table — \"0.09%\" and \"21,705,975\" respectively. The "
      + "shared number formatter now accepts scientific-notation values; follow-up to 1.9.128.",
    ],
  },
  {
    version: "1.9.128",
    date: "2026-10-16",
    notes: [
      "FIXED (Analyze charts & tables): percentages now show with the right precision. A small "
      + "rate like a 0.32% click-through rate was showing as \"0%\" on the chart (and the right-"
      + "hand axis was mis-scaled, e.g. up to 400%), while the Source Records table dumped the raw "
      + "value with a long string of decimals. Percentages now read correctly everywhere — a CTR "
      + "shows as \"0.32%\" (not \"0%\" and not \"32%\"), the chart's secondary axis is sensibly "
      + "scaled to the data, and the table no longer shows raw decimals. Dollar amounts, counts, "
      + "dates, ratios (like a 3.5x ROAS) and already-percent columns from your file are unchanged. "
      + "All chart and table numbers now flow through a single, consistent formatter so the chart "
      + "line, its axis, the hover tooltip and the table always agree.",
    ],
  },
  {
    version: "1.9.127",
    date: "2026-09-16",
    notes: [
      "FIXED (File Upload, completing the previous release): switching projects while several "
      + "files were still importing could send the remaining files to the newly-selected project "
      + "instead of the one you started in — and for a large file it could fail the import "
      + "outright with \"Access denied\". Every step of an upload is now locked to the project it "
      + "began in, including the cleaning report, the duplicate check and the large-file progress "
      + "check, so a batch can never be split across two projects.",
      "NEW: the project switcher is now disabled while an upload batch is still importing, and "
      + "explains which project the files are going into. Everything else stays navigable — you "
      + "can move around the app freely, you just can't change the destination mid-upload.",
      "FIXED (affects every future release): a new version of the app could keep serving the "
      + "OLD code from your browser's cache until you did a manual hard refresh, because the "
      + "page shell was cacheable without revalidation. Releases are now picked up on a normal "
      + "page load. As a side benefit, repeat visits load faster — unchanged code and styles are "
      + "now cached properly instead of being re-downloaded.",
    ],
  },
  {
    version: "1.9.126",
    date: "2026-09-15",
    notes: [
      "FIXED (File Upload): when uploading several files at once and switching to a different "
      + "project (or Working-in target) before the batch finished, the in-progress upload list "
      + "kept showing under the newly-selected project — as if those files were going there. "
      + "They weren't meant to, but the mismatch was confusing. Now an upload batch stays tied "
      + "to the project you started it in: its progress panel only shows under that project, and "
      + "every file in the batch is imported into that project no matter what you switch to while "
      + "it runs. Switching back to the original project shows the batch again.",
    ],
  },
  {
    version: "1.9.125",
    date: "2026-09-15",
    notes: [
      "FIXED (template import, 3 issues found testing the previous release): one previously-"
      + "working chart went back to \"needs data\" with garbled formula text in its title — a "
      + "date-range formula pattern common in modern Excel reports wasn't recognized as "
      + "dynamic, so the raw formula text got treated as a literal filter value. Two other "
      + "charts grouped by the wrong breakdown (2 categories shown instead of the real 4) "
      + "because a previously-confirmed-but-stale mapping was accepted even though a clearly "
      + "better-matching column existed in your data. And a rate chart (\"Recall jobs / "
      + "completed jobs\") displayed as a raw decimal (e.g. \"0.09\") instead of a percentage "
      + "(\"8.9%\") — percentage-styled Excel columns weren't being recognized as percentages "
      + "at all for charts (Pinned Tables already handled this correctly). Note: the source "
      + "Excel file itself uses 1 decimal place for this percentage column, not 3 — the chart "
      + "now matches the file's own actual precision.",
    ],
  },
  {
    version: "1.9.124",
    date: "2026-09-14",
    notes: [
      "FIXED: a template chart with a month/date category (e.g. \"Monthly revenue and job "
      + "gross profit\") could render its months out of calendar order (e.g. Aug, Feb, Jul, "
      + "Mar...) instead of chronologically, when the underlying data used an abbreviated "
      + "month label like \"Sep 25\" rather than a full date. Months (and quarters) now sort "
      + "in the correct calendar order regardless of that formatting.",
      "FIXED (backend, template import): investigated a customer report where only 2 of 6 "
      + "template charts imported, and one of those two showed the wrong numbers. Root-caused "
      + "and fixed three separate issues: a chart's measure could be dropped when its column "
      + "name existed in more than one of your data categories; SUMIFS formulas referencing an "
      + "Excel Table (a common modern Excel authoring style) weren't recognized at all; and a "
      + "chart whose Excel file had no saved calculation cache had no way to double-check its "
      + "category against your real data. All three are fixed — the customer's file now "
      + "imports 5 of 6 charts correctly (the 6th has a formula shape not yet supported and is "
      + "correctly flagged low-confidence rather than shown with wrong numbers).",
    ],
  },
  {
    version: "1.9.123",
    date: "2026-09-13",
    notes: [
      "IMPROVED: the Template confirm screen no longer shows a \"Values differ from original\" "
      + "warning — this was confusing since your live data is always current and can legitimately "
      + "differ from a template's saved snapshot; only a clear \"Matches original\" confirmation "
      + "shows now. Added a short explainer distinguishing what \"confidence\" means (which column "
      + "we matched) from \"matches original\" (does the live number agree with your file's saved "
      + "value).",
      "IMPROVED: refreshing the dashboard now visibly dims the board and shows a \"Refreshing...\" "
      + "banner while the new data loads, instead of leaving the old numbers looking current.",
      "IMPROVED: the Analyze chat input box now stays anchored at the bottom of the screen even "
      + "on a long conversation, instead of only being reachable by scrolling all the way down.",
    ],
  },
  {
    version: "1.9.122",
    date: "2026-09-13",
    notes: [
      "FIXED: a Pinned Table row label that looks like a range (e.g. an age bracket like "
      + "\"25-34\") was showing only its first number (\"25\") on the dashboard instead of the "
      + "full range text. Excel export and PowerPoint export always showed it correctly — only "
      + "the in-app table had this issue. Row labels like this now display in full.",
    ],
  },
  {
    version: "1.9.121",
    date: "2026-09-13",
    notes: [
      "IMPROVED: Pinned Tables have a more polished look — a bolder header, alternating row "
      + "shading, a distinct highlighted Total/Grand-Total row, and a header that stays visible "
      + "while you scroll a tall table. This same look now also carries over when you export to "
      + "Excel or PowerPoint — Pinned Tables previously didn't appear in either export at all.",
    ],
  },
  {
    version: "1.9.120",
    date: "2026-09-13",
    notes: [
      "FIXED: pinning a native pivot table (like an Age breakdown with a grand total row) "
      + "directly from your spreadsheet showed the SAME grand-total number in every row instead "
      + "of each row's own value. Each row now shows its own correctly filtered number, and row "
      + "labels display as text instead of being mistaken for data.",
    ],
  },
  {
    version: "1.9.119",
    date: "2026-09-13",
    notes: [
      "FIXED: a Total row's ratio cell that depends on ANOTHER Total-row ratio cell (a chain "
      + "two levels deep, like CPM/CTR built on sub-totals that are themselves built from row "
      + "sub-totals) could get stuck showing \"This cell -- formula uses an unsupported "
      + "function/shape\" even though every number it needed was already correct. These now "
      + "resolve to the right value automatically, no matter how many levels deep the chain goes.",
    ],
  },
  {
    version: "1.9.118",
    date: "2026-09-13",
    notes: [
      "FIXED: percentage cells in Pinned Tables (like CTR) showed the wrong number — e.g. "
      + "0.157% was showing as 0.002%. Percentages now display correctly.",
      "FIXED: a ratio cell dividing a Total row by another value it depends on (like a CPM or "
      + "CTR cell in a Total row) could show as unresolved even though both numbers it needed "
      + "were already correct — this now computes the number correctly.",
      "IMPROVED: when a table cell can't be resolved because a column from your file isn't "
      + "mapped yet, the explanation now shows the actual column name instead of a generic "
      + "\"This cell\" placeholder, and groups repeats of the same missing column together.",
    ],
  },
  {
    version: "1.9.117",
    date: "2026-09-03",
    notes: [
      "FIXED: some Pinned Table row labels showed blank instead of the row's real name (like a "
      + "creative/ad name) — the label was mistaken for a data value the table tried to total "
      + "up, instead of the text it actually is. Row labels now always show correctly.",
      "FIXED: a Total row's own ratio cells (like CPM, CTR, engagement rate) showed as "
      + "unresolved instead of the calculated number — these now compute correctly from the "
      + "Total row's already-correct totals, with the right currency/percent formatting.",
    ],
  },
  {
    version: "1.9.116",
    date: "2026-09-03",
    notes: [
      "FIXED: a Pinned Table Total row summing several rows that each apply their own filter "
      + "(like one row per creative/ad) could show a wildly inflated number — confirmed on a "
      + "real table, a $7,999.87 total was showing as $770,965.04. Totals like this now add up "
      + "each row's own already-correct value instead of re-querying with the wrong scope.",
      "FIXED: ratio-style table cells (CPM, CTR, engagement rate) showed as plain unformatted "
      + "numbers instead of currency/percentages, and the decimal precision now matches your "
      + "source Excel file's own formatting (e.g. 3 decimals for a small percentage, instead of "
      + "always rounding to 1) rather than a fixed default.",
    ],
  },
  {
    version: "1.9.115",
    date: "2026-09-12",
    notes: [
      "IMPROVED: the pivot-table list shown when adding summary tables from your template now "
      + "scrolls inside its own box instead of stretching the whole window when your file has "
      + "many of them.",
      "FIXED: a chart pinned from Analyze to your dashboard with grouped bar-header labels "
      + "(like age-group bands) now keeps those labels — they were being dropped when pinned.",
      "NEW: re-editing your template no longer drops the summary tables already on your "
      + "dashboard. They're now automatically carried forward with fresh data from your new "
      + "file, tagged \"Already on your dashboard\" — just remove any you don't want to keep.",
    ],
  },
  {
    version: "1.9.114",
    date: "2026-09-12",
    notes: [
      "FIXED: a Pinned Table's Total/subtotal rows could show an inflated, wrong number — e.g. "
      + "two DIFFERENT total rows showing the exact same value. The underlying calculation now "
      + "correctly keeps each row's own scope (like a specific channel or campaign) when "
      + "combining several rows into one total.",
      "FIXED: some table cells showed a raw, unformatted number — scientific notation like "
      + "'4.4999999999999998E-2' or a long unrounded decimal — instead of a clean, formatted "
      + "value. Numeric cells now always display and align the same way, whether the value came "
      + "from your file directly or from a calculation.",
      "NEW: charts and tables on your dashboard can now be maximized to a larger view (the "
      + "expand icon on each card) — useful for a table with many columns or a chart that's hard "
      + "to read at the normal card size.",
      "IMPROVED: the 'things to clarify' list when adding a table from your template now groups "
      + "cells that show the same unmatched word together, instead of listing every cell "
      + "separately — answering once now applies to every matching cell, and the list is "
      + "collapsed by default when there's more than a couple of things to review.",
    ],
  },
  {
    version: "1.9.113",
    date: "2026-09-11",
    notes: [
      "FIXED: restoring or uploading a very large file (300K+ rows) could show a \"still working, "
      + "refresh in a minute\" message even though it actually finished successfully seconds later. "
      + "The app now waits long enough (about 5 1/2 minutes) to see the real result before giving up, "
      + "instead of guessing too early.",
      "IMPROVED: uploading a large raw data file (Excel or CSV) is now processed in the background "
      + "and reports an honest success or failure message, instead of risking a silent timeout.",
    ],
  },
  {
    version: "1.9.112",
    date: "2026-09-03",
    notes: [
      "FIXED: closing the 'Upload My Template' window after your charts were extracted now shows "
      + "a clear, styled confirmation ('Discard this template?') instead of a plain browser popup — "
      + "same look as every other confirmation in the app.",
      "FIXED: table cells with more advanced math (like a ratio between two summary values, or a "
      + "total mixing a target with real data) now resolve correctly instead of showing as unmatched.",
    ],
  },
  {
    version: "1.9.111",
    date: "2026-09-09",
    notes: [
      "FIXED: a table you added from your uploaded template (KPI/summary tables, not charts) "
      + "now actually shows up on your dashboard — it was being saved when you published, but "
      + "wasn't appearing anywhere. It now refreshes automatically whenever your data updates, "
      + "the same way your charts already do.",
      "FIXED: adding a table whose cells use simple math (like a ratio) referencing a summary/"
      + "pivot table in your file no longer fails with 'unsupported formula' — and you're no "
      + "longer asked to explain what a plain number means.",
    ],
  },
  {
    version: "1.9.110",
    date: "2026-09-09",
    notes: [
      "NEW: 'Upload My Template' now has a new step to add summary/KPI tables from your report, "
      + "not just charts — after confirming your charts, you can check off any table Boomerang "
      + "found automatically, or type in a range for a table you built yourself. This step is "
      + "optional — skip it if your report only has charts.",
      "NEW: if a table has a word or label Boomerang couldn't match to your data, you can now "
      + "explain what it means right there — Boomerang proposes a formula for you to review "
      + "before confirming, and it's remembered for every future report so you never have to "
      + "explain it again.",
      "NEW: 'Manage Concepts' — a new option in the template menu (next to 'Edit Template') to "
      + "see, and retire, every explanation you've given so far for this project.",
    ],
  },
  {
    version: "1.9.109",
    date: "2026-09-07",
    notes: [
      "FIXED: a real gap behind 'the wizard said 6 charts matched but only 1 previewed / 2 "
      + "published' — a memory of previously-confirmed column matches (which carries across "
      + "your different projects to save re-confirming) was being trusted even when the "
      + "remembered column didn't exist in the current project's data, silently binding charts "
      + "to a column that wasn't there. It now double-checks the remembered column is actually "
      + "present before trusting it, so this project's own real columns win instead.",
      "The match-confidence pills on each chart in 'Confirm Your Template' now say WHICH field "
      + "they're rating (e.g. 'Category (breakdown)' or the series' own label like '25%') "
      + "instead of showing an unlabeled row of colored badges — reported as still unclear which "
      + "pill meant what.",
    ],
  },
  {
    version: "1.9.108",
    date: "2026-09-07",
    notes: [
      "'Upload My Template' now shows a real chart preview before you publish — click 'Preview' "
      + "on any chart in the confirm screen to see exactly what it will look like against your "
      + "data, instead of only confidence pills and a match badge.",
      "FIXED (real bugs found live-testing an actual customer report): a chart whose measure "
      + "was actually present in your data could show a false 'needs data' — a column name "
      + "mismatch bug where an already-clean column name got re-processed and mismatched (e.g. "
      + "an internal 'amount_spent__usd' vs 'amount_spent_usd' typo). Also fixed two identical "
      + "charts (same breakdown, same numbers) both getting published as duplicate cards, and a "
      + "multi-part chart (like a video-views-by-quartile breakdown) where several parts could "
      + "end up pointing at the same column instead of their own distinct ones.",
    ],
  },
  {
    version: "1.9.107",
    date: "2026-09-06",
    notes: [
      "'What's New' now shows just the 5 most recent releases by default, with a 'Show full "
      + "history' button to expand the rest — the full list had grown long enough that opening "
      + "it always meant a lot of scrolling to see what actually changed recently.",
    ],
  },
  {
    version: "1.9.106",
    date: "2026-09-06",
    notes: [
      "Moved 'File Upload' in the left menu to sit below 'Manage projects' instead of above it "
      + "— groups it with project management, which is where it's used from. No functional "
      + "change, just position.",
    ],
  },
  {
    version: "1.9.105",
    date: "2026-09-06",
    notes: [
      "Legacy .xls (older Excel format) files now upload correctly — they previously failed "
      + "with a generic error even though .xls was listed as supported. Also: a file that can't "
      + "be uploaded now shows exactly which file types ARE supported (.csv, .xlsx, .xls) instead "
      + "of a generic error, and the file list's 'drag a file anywhere' hint shows the supported "
      + "types on hover.",
    ],
  },
  {
    version: "1.9.104",
    date: "2026-09-06",
    notes: [
      "'Upload My Template' now explains what the High/Medium/Low match-confidence pills mean "
      + "(a small legend plus a tooltip on each pill) — reported as unclear what those labels "
      + "were rating. They describe how sure the system is that a chart's field was matched to "
      + "the right column in your data, not the data's quality or importance.",
    ],
  },
  {
    version: "1.9.103",
    date: "2026-09-05",
    notes: [
      "A chart from your uploaded template now shows a small purple accent on its left edge, so "
      + "it's clear which cards came from your template versus Boomerang's auto-selected ones, "
      + "even outside the 'From Template' section (e.g. in a drill-down view). Also improved the "
      + "message on a template chart that needs a breakdown your data doesn't have (e.g. an age "
      + "or region column your report used but wasn't uploaded) — it now says so plainly instead "
      + "of suggesting you re-upload a file, which wouldn't fix it.",
    ],
  },
  {
    version: "1.9.102",
    date: "2026-09-04",
    notes: [
      "Redesigned (pilot): 'Upload My Template' moved off the Analyze chat (where it sat below a "
      + "long conversation history) to a compact control in the Dashboard toolbar — an icon-only "
      + "button before you have a template, and a small 'Template active' pill with Edit/Switch "
      + "actions once you do. Progress now shows as a corner-anchored activity pill (like a file "
      + "upload notification) instead of an in-page banner, so you can navigate freely while your "
      + "template extracts and still see it complete from anywhere in the app.",
    ],
  },
  {
    version: "1.9.101",
    date: "2026-09-04",
    notes: [
      "Fixed (pilot): the previous fix only covered clicking outside the dialog — clicking any "
      + "main menu item while your report was still being extracted still closed the whole thing "
      + "and lost the result. Uploading a template now keeps working in the background no matter "
      + "what you click or navigate to, and Analyze shows a clear status card ('Processing your "
      + "template…' → 'Your template is ready to review') so you always know where it stands and "
      + "can pick it back up from there — even after closing the dialog or switching pages.",
    ],
  },
  {
    version: "1.9.100",
    date: "2026-09-04",
    notes: [
      "Fixed (pilot): a stray click outside the 'Upload My Template' dialog while your report "
      + "was still being extracted (this can take 20-30+ seconds for a large multi-chart file) "
      + "closed the dialog and discarded the result, with nothing saved anywhere — reported as "
      + "\"the upload did something but I never saw the result.\" The dialog no longer closes on "
      + "an outside click while extraction is running, and closing it once your charts are ready "
      + "now asks you to confirm first, so you can't lose a completed extraction by accident.",
    ],
  },
  {
    version: "1.9.99",
    date: "2026-09-04",
    notes: [
      "Fixed (pilot): 'Upload My Template' now shows a visible progress indicator (spinner + "
      + "stage text) while extracting a large report's charts, instead of a static message that "
      + "could look stuck for up to a minute on a report with many charts. Also fixed a real "
      + "publish-blocking bug found testing an actual customer report: charts using a computed "
      + "metric your report already uses (CPM, CTR, CPV, VTR) could fail to publish with "
      + "'measure does not resolve' — those metrics now correctly bind to your matched columns "
      + "when reviewed and confirmed. Also corrected chart titles that could run words together "
      + "without spaces when a title wrapped onto a second line in your original file.",
    ],
  },
  {
    version: "1.9.98",
    date: "2026-09-04",
    notes: [
      "Fixed (pilot): 'Upload My Template' could silently do nothing when a file was rejected "
      + "by the browser's own file-type check (no error, no request sent) — most likely to "
      + "happen with a .xlsm whose reported file type varies by browser/OS. The dialog now shows "
      + "a clear message instead of leaving you staring at an unresponsive upload box.",
    ],
  },
  {
    version: "1.9.97",
    date: "2026-09-04",
    notes: [
      "Fixed (pilot): 'Upload My Template' now uploads directly to storage instead of embedding "
      + "the file in the request, raising the real limit from an unreachable 25MB (files over "
      + "roughly 7MB were silently rejected by the network layer before reaching our own "
      + "friendly error) to a genuinely usable 50MB. Also cleaned up the Analyze toolbar so a "
      + "template board no longer shows two overlapping mode indicators ('Template Active' and "
      + "'Analyzing as: Blueprint') — the domain switcher (which doesn't apply to a template "
      + "board) is now hidden while a template is active instead of duplicating the same status.",
    ],
  },
  {
    version: "1.9.96",
    date: "2026-09-03",
    notes: [
      "New (pilot): the earlier 'Smart (AI) | My layout' toolbar toggle is replaced by a single, "
      + "contextual entry point on your Analyze dashboard — a banner offering 'Upload My Template' "
      + "when you have no custom template yet, or a compact 'Template Active' indicator (with "
      + "Edit/Switch back to Smart AI) once you do. Uploading a template walks you through one "
      + "guided wizard: upload your Excel/PowerPoint report, confirm any ambiguous matches "
      + "side-by-side with a live preview against your real data, and publish. Your board now "
      + "always shows two clearly labeled sections — your AI-selected or template charts, and 'My "
      + "Additions' (anything you've pinned yourself) — so publishing a template never touches "
      + "your own pinned charts. Archiving a file that feeds a published template now warns you "
      + "with the number of affected charts before you confirm. Only visible for accounts where "
      + "this feature is enabled.",
    ],
  },
  {
    version: "1.9.95",
    date: "2026-08-31",
    notes: [
      "New (pilot): you can switch a project's board between 'Smart (AI)' — today's auto-selected board — and 'My layout', which reproduces a fixed set of charts in your own order. The toggle appears in the board toolbar only for accounts where the feature is enabled, so nothing changes for everyone else. When a chart's data isn't available, its slot stays in place with a clear 'Needs data' explanation instead of disappearing or showing a made-up number.",
    ],
  },
  {
    version: "1.9.94",
    date: "2026-08-29",
    notes: [
      "Admin → Models is now a real model switcher. A 'Global default model' control lets you pick the Smart and Fast models from a dropdown of what's available in your account and Save — it applies to everyone immediately, with no redeploy (and a one-click reset to the build default). You can still trial a candidate on a single tenant first. Plus a clear SmartModel/FastModel summary with per-1M pricing, and a table showing which model each activity uses (SQL generation, interpretation, classification, …) and how much a smarter model helps each.",
    ],
  },
  {
    version: "1.9.93",
    date: "2026-08-28",
    notes: [
      "Restoring an archived file now shows real progress and finishes on its own. Before, restoring a large file left the button sitting there with no feedback and you had to click Refresh to see it come back — because re-processing a big file took longer than the server's request limit. Restore now runs in the background: the button shows a 'Restoring…' state, and when it's done the file automatically appears in your Active list with a confirmation (or a clear 'nothing to restore' / error message). No more manual refresh.",
    ],
  },
  {
    version: "1.9.92",
    date: "2026-08-27",
    notes: [
      "Admin Data Health now shows the date each flagged file was recorded (e.g. 'Aug 25, 2026 · 2d ago') on the right of each item, so you can tell an old issue from one that just happened.",
    ],
  },
  {
    version: "1.9.91",
    date: "2026-08-27",
    notes: [
      "Fixed the board spinner stopping early: right after clicking Regenerate/Refresh, the progress tracker could briefly read the PREVIOUS (finished) board before the new generation started and switch the spinner off. It now holds the spinner on until it actually sees the new generation begin, so it keeps spinning across both sections until every card is done.",
      "The Admin 'Per-customer usage' panel now auto-refreshes every 30 seconds while it's open (and when you return to the tab), so status, files, questions and last-active stay current without clicking Refresh.",
    ],
  },
  {
    version: "1.9.90",
    date: "2026-08-27",
    notes: [
      "Reworked the board 'refreshing' spinner so it's reliable across window and section switches. Instead of depending on whichever page is open (which broke when you switched away and back), a single app-wide tracker now polls the board's real progress on its own and drives both the Analyze 'Regenerate board' and Dashboard 'Refresh' spinners together — so they stay in sync and keep spinning until the board is actually finished, no matter where you navigate.",
    ],
  },
  {
    version: "1.9.89",
    date: "2026-08-27",
    notes: [
      "Fixed the board 'refreshing' spinner for real: when returning to Analyze or Dashboard the board view reloads for a moment, and that reload was briefly reporting 'not working' and stopping the shared spinner even though a rebuild was still in progress. It now only reports 'done' once a fully-loaded board is actually finished, so the Regenerate/Refresh spinner keeps spinning across section switches until every card is ready.",
    ],
  },
  {
    version: "1.9.88",
    date: "2026-08-27",
    notes: [
      "Definitive fix for the board 'refreshing' spinner: it now stays correct when you switch between Analyze and Dashboard. The in-progress state lives above both sections (so it no longer resets when a section unmounts), starts the moment you click Regenerate/Refresh, and — importantly — treats the board as still working while ANY card is still generating (previously it could stop early while KPI cards were still 'processing'). Both the Analyze 'Regenerate board' and Dashboard 'Refresh' spinners are now driven by the same shared state, so they're always in sync.",
      "Definitive fix for multi-file uploads: the upload queue now lives above the page, so if you switch away mid-upload and come back to File Upload you immediately see every file with its live status (queued, processing, done) instead of a stale list — the import keeps running and reporting in the background.",
    ],
  },
  {
    version: "1.9.87",
    date: "2026-08-27",
    notes: [
      "File Upload now keeps the list in step on its own: if you upload several files and switch away, coming back (or the moments while a batch is still importing) refreshes the list automatically, so files that finished processing show up without clicking Refresh.",
      "Auto-Insights board 'refreshing' is now resilient to leaving and returning: when a browser tab is backgrounded it can freeze the board's status polling, which left the spinner looking stuck while cards were still 'processing'. The board now re-checks the moment the tab regains focus and keeps a steady backstop poll while it's generating, so the spinner reflects the real state and finished charts appear as soon as you return.",
    ],
  },
  {
    version: "1.9.86",
    date: "2026-08-22",
    notes: [
      "The board 'refreshing' spinner now follows the actual rebuild, not just the button you clicked: if you switch to Analyze or Dashboard while a refresh is still running, that section's button (Regenerate board / Refresh) keeps spinning until the board finishes. Both buttons are driven by the same live board status, so they're always in sync - both spinning or both stopped.",
    ],
  },
  {
    version: "1.9.85",
    date: "2026-08-22",
    notes: [
      "The 'Regenerate board' button in Analyze now shows the same spinning indicator while it works as the Dashboard's Refresh, so both make it obvious the board is rebuilding.",
    ],
  },
  {
    version: "1.9.84",
    date: "2026-08-22",
    notes: [
      "Dashboard 'Refresh' now fully rebuilds your board from the latest data - exactly like 'Regenerate board' in Analyze - instead of only re-checking for a new data version.",
      "Added a clear progress indicator while a refresh runs: the Refresh button shows a spinner and 'Refreshing…', and a banner appears over the board so it's obvious the rebuild is in progress (charts update as they finish).",
      "Auto cards that can't be built from your current data are now hidden entirely, so the board only shows metrics that actually computed. (A chart you pinned yourself still shows a 'Needs data' note if it needs attention.)",
    ],
  },
  {
    version: "1.9.83",
    date: "2026-08-22",
    notes: [
      "Streamlined the Dashboard controls: removed the in-design preview items (multiple named dashboards and the dashboard-level date range) so the top bar shows only working controls - project, the All/Pinned/Auto filter, Add from AI Insights, Refresh, and Export. These features will return once they're fully built.",
    ],
  },
  {
    version: "1.9.82",
    date: "2026-08-22",
    notes: [
      "Fixed: a chart pinned to the dashboard from Analyze now looks exactly like the one you saw in the answer. Previously, if the answer's chart was aggregated (e.g. rolled up to 3 monthly bars), the pinned copy re-rendered the raw daily rows and looked completely different. It now pins the exact plotted data.",
    ],
  },
  {
    version: "1.9.81",
    date: "2026-08-22",
    notes: [
      "KPI cards now show a period-over-period trend when your data has dates: a small sparkline behind the number and a green/red change pill (e.g. '▲ 8.2% vs prior mo.'). The change is computed correctly from your monthly totals - for rates like CTR or ROAS it's recomputed each month, never a fabricated number. Metrics without a date column simply show the value, as before.",
      "Cleaner card takeaways: removed a stray '# Executive Takeaway' heading that was sometimes appearing at the start of a card's summary.",
      "Streamlined the Dashboard: the Scenario & Goals panel and the Schedule-digest button were removed from the Dashboard view to keep it focused on reviewing and exporting your board.",
    ],
  },
  {
    version: "1.9.80",
    date: "2026-08-22",
    notes: [
      "Fixed a confusing sync between Analyze and the Dashboard when adding/removing a chart from your board. Removing a pinned chart (from either place) now always resolves cleanly, and the 'Added' badge in Analyze can no longer get stuck showing 'Added' after you removed the chart elsewhere. The board also re-checks itself when you return to the tab, so what you see stays in step with your board.",
    ],
  },
  {
    version: "1.9.79",
    date: "2026-08-22",
    notes: [
      "Dashboard export now offers three formats from one menu: PowerPoint (editable native charts), Excel (a workbook with your board's data - a summary sheet plus one sheet per chart), and PDF (a clean print view of the board).",
      "The executive summary now shows a small status badge on each driver when there's a real signal - green 'On track' or red 'Needs attention' for a KPI measured against its goal, or amber 'Watch' when it's flagged as an anomaly.",
      "Added a short, dismissible note on the Dashboard explaining that the board is built from your current uploaded data and refreshes when your data changes (and that chat answers in Analyze build on your recent questions).",
    ],
  },
  {
    version: "1.9.78",
    date: "2026-08-22",
    notes: [
      "The AI Insights Dashboard now uses the full width of the screen instead of a narrow centered column, so your board and charts fill the working area.",
      "Cleaner executive layout: the top controls (project, All/Pinned/Auto filter, date range, Add, Refresh, Schedule, Export) are consolidated into one cohesive bar, and each card's tools (pin, info, regenerate, remove) now appear on hover instead of cluttering every card.",
      "The executive summary is now split into Key performance drivers and Anomalies & risks, and each point links straight to the chart it came from - click to jump to that card.",
      "KPI cards show a clear green/red status pill when the metric has a goal set (vs target).",
      "A metric that can't be computed from the current data now shows a compact Needs data note with one-click View data logic and Ask in Analyze actions, instead of a large error message.",
      "The Analyze and Dashboard items under AI Insights are indented a bit more so the grouping reads clearly.",
    ],
  },
  {
    version: "1.9.77",
    date: "2026-08-22",
    notes: [
      "Charts that break a result down by an entity (e.g. campaigns) now group those bars by their parent company or platform: bars for the same company sit together as a block, with the company name labeled in a band above them and a light divider between companies. So a question like 'compare underperforming campaigns and include the company' now reads at a glance — you can see which campaigns belong to Amazon vs Walmart — instead of a flat, unlabeled row of bars.",
    ],
  },
  {
    version: "1.9.76",
    date: "2026-08-22",
    notes: [
      "Chart x-axis labels no longer get skipped. On a chart with several categories (e.g. 6 campaigns), every bar now shows its own label — angled for readability — instead of the chart hiding every other name. All the data was always there; only the labels were being thinned.",
      "Chart hover tooltips are now clearly legible: the title (e.g. the campaign name) is dark and bold on a clean white card, instead of the previous faint gray that was hard to read.",
    ],
  },
  {
    version: "1.9.75",
    date: "2026-08-21",
    notes: [
      "AI Insights is now split into two spaces in the left menu: Analyze (ask questions and build charts, as before) and a new full-screen Dashboard. The Dashboard gives your auto-generated board and pinned charts their own dedicated page with a project selector, an All / Pinned / Auto filter, Refresh, and Export to PowerPoint. A few power-user controls (multiple named dashboards, a dashboard-wide date range, and scheduled email digests) are shown as clearly-marked previews while we build them.",
    ],
  },
  {
    version: "1.9.74",
    date: "2026-08-21",
    notes: [
      "AI Insights now gives the SAME answer every time for the same question and data. Previously a rate like CTR could come back slightly different from one run to the next (e.g. 4.68% one time, 4.27% another) because the AI had freedom to phrase the calculation differently — now the calculation is locked, so 'show ctr recap of meta vs google' returns a consistent, correct result each time.",
      "Rates (CTR, CPC, conversion rate, etc.) are now always calculated the right way — as total clicks ÷ total impressions across the whole selection, never a simple average of per-row percentages (which over- or under-weights small rows). This also fixes cases where comparing two sources with differently named columns could show 0%.",
      "Rate comparisons now draw a richer chart by default: the volume (e.g. clicks and impressions) shows as bars with the rate as a line on a second axis, so you see both the size and the efficiency at a glance — and can see exactly what the rate was calculated from.",
      "Added a gentle reminder under the question box: follow-up questions build on the current conversation, so earlier questions can shape a new answer or chart. A one-click 'clear history' lets you ask a fresh, independent question.",
    ],
  },
  {
    version: "1.9.73",
    date: "2026-08-17",
    notes: [
      "AI Insights is faster and clearer when a question is complex. Behind the scenes, answers now come back well within the time limit instead of occasionally failing on heavy questions, and if something does go wrong you'll see a friendly, plain-language message ('That question took longer than expected — try again, or narrow it to one metric or period') instead of a technical error. Time-based questions are smarter too: asking for 'Q2', 'last quarter', 'March', or 'year to date' now matches your data even when it's stored with the year (e.g. 2026-Q2). And a plan-vs-actual comparison now always draws a clear, consistent grouped chart rather than occasionally stacking values that shouldn't be added together.",
    ],
  },
  {
    version: "1.9.72",
    date: "2026-08-05",
    notes: [
      "Rebalanced the app's look so it's easier on the eyes: the left sidebar and top bar are now a softer slate-indigo instead of near-black, and the working area is a calm off-white instead of a stark dark background — so your charts, tables and cards no longer sit against a harsh contrast. The currently selected menu item is more clearly highlighted",
      "Redesigned File Upload to be much leaner and calmer. The three large stat cards and the full-width security banner are gone — replaced by a single slim header with compact 'active' / 'archived' counts and a small lock icon (hover for the encryption / isolation / duplicate-detection / recoverable-archive details). Active and Archived are now tabs in one card (no separate section at the bottom), with a search box and a '＋ Add file' button — and you can drag a file anywhere onto the card to upload. Each file is a clean single-line row showing an at-a-glance status chip (green 'Clean' / amber 'Review') with a 'Cleaning report' link that expands the full profiling & cleaning detail (verdict shown first, the rest tucked under it); the per-file actions (Reprocess, Move, Archive, Download / Restore) now live in a compact ⋯ menu instead of a row of competing buttons. Also corrected the wording to 'powers your AI Insights' (was an outdated 'QuickSight' reference)",
      "You can now upload multiple files at once. Select several files (or drag them onto the card) and they import one after another, with a live queue showing each file's progress ('Uploading 2 of 5') and a summary at the end — one flagged or failed file no longer stops the rest. (Files process one at a time to keep de-duplication accurate and reliable.)",
      "Fixed: regenerating the Auto-Insights board (or refreshing it) no longer clears your AI Insights conversation. Your asked questions and answers now stay on screen through a board rebuild — the chat is only cleared when you explicitly use 'Clear history' / start a new chat, or switch to a different project",
      "Clearer cleaning report: it now always shows all three phases — the Integrity check (phase 3) leads at the top as the ✓ Passed / ⚠ Review verdict and is also numbered under Details, so it no longer looks like a phase is missing. For an already-clean file (nothing to remove or standardize) the report now says so explicitly ('Already clean — no duplicates, invalid rows, or values needed standardizing') instead of showing a near-empty section",
      "Auto-Insights board loading feels calm, never 'failed': the very first time a board is built (especially for a project with many files/tables) it now shows a friendly 'Building your executive board from your data…' progress state and keeps checking automatically until the charts appear, instead of briefly flashing a red 'Couldn't load the board' error while it's still working. Files split into separate reports by type now analyze cleanly too, so a set of related uploads (e.g. campaign, device, landing-page reports) each get their own correct charts",
    ],
  },
  {
    version: "1.9.71",
    date: "2026-08-05",
    notes: [
      "Value labels now also show on the Auto-Insights board's bar charts (e.g. 'Revenue by Advertiser'), matching the chat and the other chart types. Very dense charts (more than ~24 points, like a multi-year monthly line) still omit per-point labels because they'd overlap and be unreadable",
      "The 'Add to board' button is now accurate: when you return to AI Insights it correctly shows '✓ Added' for charts that are actually on your board (previously the marker reset every time you reloaded), and you can now remove a chart from the board directly from the chat with a new 'Remove' button",
    ],
  },
  {
    version: "1.9.70",
    date: "2026-08-04",
    notes: [
      "Export to PDF is now fast (it used to take a long time even for one chart): the chart is captured directly from its vector form instead of screenshotting the whole page, so it's near-instant and looks identical to what's on screen (a legend is included). Also removed a heavy library that's no longer needed, so the app loads a bit faster",
      "Value labels now appear on line and area charts too (not only bars), and show on typical monthly charts (previously they were hidden once a chart had more than 12 points) — so you can read the numbers directly on any chart, on-screen and in exports",
      "Your manually pinned charts now survive a data update: when you upload new data, the charts you added to your board are carried over and refreshed against the new data automatically, instead of disappearing. If a pinned chart can't be refreshed (for example a column it used is gone), the board tells you so you can re-add it — pinned charts never show stale numbers",
    ],
  },
  {
    version: "1.9.69",
    date: "2026-08-04",
    notes: [
      "Fixed: when you pinned a combined (dual-axis) or stacked chart to your board with 'Add to board', the board and the PowerPoint export showed it as plain side-by-side bars instead of the dual-axis / stacked chart you saw live. Pinned charts now keep their exact shape everywhere (on-screen board and PPTX). Note: charts pinned before this fix should be removed and re-added to correct them",
      "Added value labels on the bars of combined and stacked charts (on-screen and in the PowerPoint export) so the numbers are readable at a glance",
    ],
  },
  {
    version: "1.9.68",
    date: "2026-08-03",
    notes: [
      "Cleaner, roomier AI Insights: the top of the page is now a single slim row (title, the Auto-Insights badge, a subtle 'updated' note, the project selector and Clear history all on one line) instead of several stacked rows — so your questions, answers and charts get more vertical space. The Auto-Insights banner, the board action toolbar, and the collapsible Insight Engine panel are unchanged",
      "When you ask for a combined/stacked chart that your data can't support, the short explanation of WHY is now shown in bold so it's easy to spot (instead of blending into the answer)",
      "Fixed a percentage axis showing an inflated value (e.g. a 100% return rate rendering as '10000%'): a rate that's already stored in percentage points is now shown correctly, in charts and in the PowerPoint export",
    ],
  },
  {
    version: "1.9.67",
    date: "2026-08-03",
    notes: [
      "New (behind the advanced-charts rollout switch): you can now get combined charts from a typed AI Insights question. Ask something like \"show revenue and margin % by month\" and, when your data fits, the answer comes back as a dual-axis chart (magnitude bars + a rate/percent line on a second axis) or a stacked column — the same executive styling as the board. If your data doesn't support a combined chart, the assistant now explains why and what would enable it, instead of quietly giving a plain chart",
    ],
  },
  {
    version: "1.9.66",
    date: "2026-08-02",
    notes: [
      "New (rolling out gradually): Auto-Insights can now build executive combination charts — a stacked column for same-unit parts that add to a whole, and a dual-axis chart that pairs a magnitude (bars, e.g. revenue or units) with a rate or percent (a line on a second axis, e.g. margin %). These appear automatically when your data has the right shape, both on the board and in the PowerPoint export. Available behind a per-account rollout switch (off by default); everything else is unchanged when it's off",
    ],
  },
  {
    version: "1.9.65",
    date: "2026-08-02",
    notes: [
      "File Upload now uses the same clear in-app messages as the rest of the app instead of the plain browser pop-ups (which showed a confusing site-address line). Archiving, moving, restoring, reprocessing, downloading and the pre-upload quality / data-integrity checks now show a titled confirmation card (with distinct action buttons) or a green/amber/red notification — no more raw browser alerts or prompts anywhere on the page",
      "Made the pinned Auto-Insights toolbar easier to keep track of while scrolling: it now has a soft tinted background so it stands out against the white chart and text cards below it, instead of blending into them",
    ],
  },
  {
    version: "1.9.64",
    date: "2026-08-01",
    notes: [
      "Fixed the Auto-Insights board (and other feature-gated sections) sometimes disappearing right after clicking 'refresh for new version' — on that reload the app checks your enabled features, and if that check hit a brief hiccup (e.g. the server waking up) it left everything switched off until a hard refresh. The check now retries a few times and never blanks out features you already had, so the board and its auto-generated charts stay put after a version refresh",
      "Replaced the plain browser 'Clear history' confirmation (which showed a confusing site-address line) with a clear in-app dialog: a titled card explaining exactly what Clear history does and that it can't be undone, with distinct Cancel and red 'Clear history' buttons — and a green confirmation once it's done. This is the standard confirm style rolling out across the app",
      "Cleaner chart axis labels across Auto-Insights charts and the PowerPoint export: the time axis now reads 'Jan 2025' instead of '2025-01' (and 'Jan 6, 2025' / 'Q1 2025' for daily/quarterly views), and money axis values compact to '$80k' / '$1.2M' instead of '$80,000' — so charts read like an executive report at a glance. Exact values are unchanged in tables, tooltips and the data appendix",
      "The Auto-Insights toolbar (Analyzing as / Brand colors / Export deck / Regenerate board) now stays pinned to the top as you scroll the board, so those actions are always one click away instead of scrolling out of view",
    ],
  },
  {
    version: "1.9.63",
    date: "2026-07-29",
    notes: [
      "Fixed 'Add to board' adding the same chart multiple times on repeated clicks: the button now locks while it's working (shows 'Adding…') and switches to a disabled '✓ Added' once it's on the board, so a chart can't be pinned twice by accident",
    ],
  },
  {
    version: "1.9.62",
    date: "2026-07-29",
    notes: [
      "Made the AI Insights notifications clearly visible: they now appear as a solid white card with a colored accent bar (green success / amber warning / red error), centered just below the top bar so they no longer overlap the header icons or blend into the dark background",
    ],
  },
  {
    version: "1.9.61",
    date: "2026-07-29",
    notes: [
      "AI Insights notifications are now clear in-app messages with a success / warning / error style (green check, amber, or red) instead of the plain browser pop-up that showed a confusing site-address line and made a success look like an error. For example, pinning a chart to your board now shows a green 'Added to your board' confirmation, and each message has a meaningful title and explanation",
    ],
  },
  {
    version: "1.9.60",
    date: "2026-07-29",
    notes: [
      "Fixed money values in AI Insights showing as raw numbers (e.g. \"760620.3699999906\" instead of \"$760,620.37\"). Amounts now format with a currency symbol and thousands separators, and any plain number shows cleanly (thousands separators, at most two decimals) instead of a long unrounded value",
      "Boomerang now recognizes Spanish (and accented) column names when deciding what a value means — e.g. Ganancias, Dividendos, Pérdidas, Ingresos, Ventas, Importe are treated as money; Fecha, Tiempo, Horario, Año as dates — so labels, tables, headline cards and the PowerPoint export format correctly for Spanish datasets, not just English",
      "Fixed 'Add to board' on an AI Insights chat chart: it no longer says \"this answer doesn't have a chart to add\" when a chart is clearly shown — the chart (and its data) is now pinned to your Auto-Insights board and included in the PowerPoint export as intended",
    ],
  },
  {
    version: "1.9.59",
    date: "2026-07-29",
    notes: [
      "Fixed large files showing a false 'Upload Failed (503)' even though they imported. A big file can take longer than the gateway's 30-second response limit, so the browser saw an error while the server quietly finished cleaning and importing it. Boomerang now recognizes this case: it shows 'Large file — finishing on the server…', verifies the file actually landed, and reports success — or, if it's still going, tells you it's processing in the background and to refresh in a moment (never asking you to re-upload data that's already in)",
    ],
  },
  {
    version: "1.9.58",
    date: "2026-07-28",
    notes: [
      "Auto-Insights and AI Insights charts now use a CFO-grade executive color palette by default for everyone — electric blue for the main series, slate grey for the prior-year/benchmark series, safety orange for targets, and forest green / crimson for positive / negative performance — instead of the previous Boomerang indigo. If you set your own Brand colors, yours are used instead; until then every account gets the executive look",
    ],
  },
  {
    version: "1.9.57",
    date: "2026-07-27",
    notes: [
      "Auto-Insights charts now use role-based brand colors: when brand colors are on, a current-vs-prior comparison uses your primary color for the current series and a muted tone for the prior one, with distinct target and threshold colors — so a board reads like an executive deck instead of a rainbow. Colors that would be too light to see are automatically darkened for legibility, and good/bad status colors stay fixed (green/red) regardless of brand",
      "KPI summary cards on the board now tint by performance when a target is set: a metric beating its target shows a soft green card with a green status dot, one missing it shows a soft red card, and everything else stays neutral navy — with a 'vs <target> target' line so the color always has a reason. Cards without a target stay neutral (nothing is ever colored good/bad without a real comparison)",
      "Exported PowerPoint decks now read like an executive report: each slide is titled '[A1]. Metric' in navy with a one-line takeaway underneath, KPI slides carry the same performance tint, and charts sit below the header without overlapping — matching the on-screen board",
    ],
  },
  {
    version: "1.9.56",
    date: "2026-07-27",
    notes: [
      "Auto-Insights controls (Analyzing as / change, Brand colors, Export deck, Regenerate) now show immediately and stay put while the board is generating or empty — previously the whole toolbar only appeared after the board finished loading, so the buttons seemed to be missing and then popped in. Export is shown disabled until the board is ready",
    ],
  },
  {
    version: "1.9.55",
    date: "2026-07-27",
    notes: [
      "Fixed Brand colors so your uploaded palette is actually used: uploading a graph image now REPLACES the swatches with just the colors found in your image (it no longer pre-fills and mixes in the default Boomerang colors), so what you save is exactly your brand — and the board charts use only those colors (with a 'Start from Boomerang' option if you'd rather tweak the defaults, and Reset to go back)",
      "Your brand colors now also apply to the charts in AI Insights answers (the chat), not just the Auto-Insights board — so a chart from a typed question matches your brand too",
      "Brand colors is now its own on/off feature (off by default) so it can be enabled per account — when off, the 'Brand colors' control is hidden and charts use the Boomerang palette",
    ],
  },
  {
    version: "1.9.54",
    date: "2026-07-26",
    notes: [
      "Fixed Auto-Insights charts rendering empty (flat bars at the baseline) even though the data was there — e.g. 'Revenue by Unit Type' showed nothing while the same question as a table/chat had values. The board now converts the warehouse's text values to numbers before drawing, so every board chart (bar, line, donut) plots correctly",
      "New CRM / Sales analysis type for Auto-Insights: projects with leads/opportunities/pipeline data (a deal, a stage, and a value) now get real headline metrics — Deals, Total Value, and Average Deal Value — plus breakdowns by source, stage and over time, instead of the generic 'General' view",
      "New: set your Brand colors for Auto-Insights charts — upload a graph image to pull its colors automatically, or pick them by hand; used in the board and the exported PowerPoint, with one-click reset to the Boomerang palette (positive/negative status colors stay fixed for readability)",
      "Auto-Insights: changing the analysis type is now a proper dropdown (e.g. General, Marketing, CRM / Sales) instead of a free-text box — pick from the available analysis lenses, no typing or guessing the exact name",
      "New: 'Add to board' on any AI Insights chat chart pins it to your Auto-Insights board and includes it in the PowerPoint export — so a one-off question you like becomes part of the executive deck (remove it anytime)",
      "Removed the Auto-Insights 'Living link' button for now (a shareable link is being reworked); Export deck (PPTX) is still available",
      "Deleting a project now fully removes everything it created, including its Auto-Insights board, saved targets and pinned custom views (previously those were left behind). The Main Project no longer offers Delete — it's your default home for data and is always kept (you can still remove its files) — instead of appearing to delete and then coming back empty",
    ],
  },
  {
    version: "1.9.53",
    date: "2026-07-26",
    notes: [
      "Fixed Auto-Insights showing \"Not enough structure in this project's data\" for projects that clearly have data — the board's charts were failing to build behind the scenes and the board wrongly reported itself as empty. Auto-Insights now generates its executive board from your data as intended (charts tolerate the occasional non-numeric value in a number column instead of failing the whole visual)",
      "The Auto-Insights empty view now tells you when a board couldn't be built (vs genuinely not enough data) and gives you a Regenerate button, so it can never get stuck with no way to retry",
      "Manage Projects now shows each file's original name (e.g. \"CRM_Data_5.26.26.csv\") instead of the internal id prefix Boomerang adds when a file is cleaned/moved — matching how File Upload already displays it",
    ],
  },
  {
    version: "1.9.52",
    date: "2026-07-26",
    notes: [
      "Auto-Insights now appears the moment it's turned on — the app re-checks your enabled features whenever you return to the tab, so toggling Auto-Insights takes effect without a hard refresh (previously the feature was only checked once at sign-in, so a mid-session change stayed hidden until a full reload)",
      "The Auto-Insights executive board now shows for returning projects that already have a conversation — the board appears at the top with your chat history right below it, instead of only showing on a brand-new/empty chat",
      "The 'Before you continue' terms check can no longer spin forever on a slow/cold load — it now retries a bounded number of times and, if it still can't read your acceptance status, lets you into the app rather than trapping you on a loading screen or wrongly re-showing the acceptance prompt",
    ],
  },
  {
    version: "1.9.51",
    date: "2026-07-26",
    notes: [
      "Fixed the 'Before you continue' terms screen sometimes reappearing after a hard refresh even though you'd already accepted — on a cold reload the check could run before your session finished refreshing and wrongly showed the prompt. It now uses a fresh session token and keeps quietly retrying instead of falling back to the acceptance screen, so an already-accepted account is never re-prompted by a timing blip",
    ],
  },
  {
    version: "1.9.50",
    date: "2026-07-26",
    notes: [
      "Cleaner Manage Projects: each project row is now a single lean row with one ⋮ actions menu (Rename, Archive, Delete) instead of three inline buttons, the redundant ACTIVE tag is gone (you're already on the Active tab), and clicking a row expands its files. New lean '＋ New project' pill. Deleting a project now shows a clear centered confirmation dialog before anything is removed",
    ],
  },
  {
    version: "1.9.49",
    date: "2026-07-26",
    notes: [
      "Hardened how AI Insights decides a column's type so numbers are always labeled correctly: the server now tells the app exactly which columns are money, dates, IDs, counts, rates or ratios, and every place values are shown (the headline card, tables, and PDF export) formats from that — so a \"$\" appears only for genuine money columns, and an ID, ZIP, phone or count is never shown as a dollar amount or a date. This closes the class of error behind the earlier ID-shown-as-money issue, not just the one card",
      "The environment indicator in the top bar is now a small, subtle status dot (with the environment name on hover) instead of a bold DEV/PROD label — cleaner and more aligned with the app's look",
    ],
  },
  {
    version: "1.9.48",
    date: "2026-07-25",
    notes: [
      "Fixed AI Insights headline cards showing an ID (or code) as a big number — e.g. a line-item ID rendered as \"$23,969,964,646.00\". The single-value card now skips identifier/date columns and picks a real metric, and it only shows a \"$\" for genuine money columns, so an ID number is never mislabeled as a cost",
      "The Insight Engine's suggested questions now refresh automatically when a project's data changes (after an upload or moving a file) and are remembered while the data is unchanged — so you no longer see suggestions from data that's no longer in the project, and revisiting a project is faster",
    ],
  },
  {
    version: "1.9.47",
    date: "2026-07-25",
    notes: [
      "Manage Projects now shows the actions (Rename, Archive, Delete) as clear inline buttons on each project row — the same style as the file actions in File Upload — instead of hiding them behind a ⋮ menu",
    ],
  },
  {
    version: "1.9.46",
    date: "2026-07-25",
    notes: [
      "Fixed a false 'Failed to move file' error on large files — moving a big file re-cleans and de-duplicates it, which can take longer than the gateway's response window, so the app showed an error even though the move actually completed. Boomerang now confirms the move really finished (it's designed to complete safely in the background) and reports the correct result instead of a false failure",
    ],
  },
  {
    version: "1.9.45",
    date: "2026-07-24",
    notes: [
      "New: download the cleaned data for any file — a 'Download' button on each file (active and archived) gives you the exact standardized, de-duplicated rows that were imported, as a CSV. Full transparency into what's on file",
      "Deleting an archived project now asks for confirmation first (with the same 'export anything you need' warning as active projects) — it was previously a single click with no prompt",
      "Behind the scenes: stronger duplicate detection across file formats — the same data uploaded as .csv and .xlsx now matches reliably even when the files differ only in invisible characters (byte-order marks, non-breaking spaces, line endings), and rows containing line breaks inside a field are no longer split or mangled",
    ],
  },
  {
    version: "1.9.44",
    date: "2026-07-24",
    notes: [
      "Uploading a file now blocks navigation until it finishes — a clear progress overlay covers the screen (including the menu) so you can't accidentally switch pages mid-upload and lose track of it; the browser also warns you before a refresh or tab-close while an upload or move is still running",
      "Flagged files now show a bold amber ⚠ REVIEW badge (or red BLOCKED) next to the name instead of a faint icon, so a data-integrity concern is obvious at a glance — hover it for the summary",
      "Removed the 'New chat' button in AI Insights to keep your conversation history front-and-center; you can still start fresh from the 'data updated' banner when your data changes",
    ],
  },
  {
    version: "1.9.43",
    date: "2026-07-24",
    notes: [
      "Uploading another file into a category that already has data no longer asks 'Add / Replace / Cancel' — the server automatically validates duplicates and adds only genuinely new rows, then shows the result. (The old prompt could also do nothing when you clicked Add.)",
      "The 'Moving file…' indicator now fully blocks the screen until the move finishes, so you can't navigate to another page mid-move",
      "Multi-sheet Excel workbooks are now declined with a clear message asking you to upload one sheet per file — so every sheet is classified and de-duplicated on its own (and no leftover 'Add/Replace' prompt)",
      "Files now show an amber ⚠ next to the name when the automatic integrity check flagged something worth a look (e.g. a real number that became 0, or unexpected data loss) — hover it for a one-line summary, and open the cleaning report for details",
      "Money and other numeric values are no longer wrongly zeroed: a column the AI classified as an amount is trusted as an amount, so a value like 1226.87 stays intact instead of being mistaken for a phone number and cleared",
      "Clearer buttons in AI Insights — 'Clear' is now 'Clear history', the data-updated banner's action is now 'Start fresh with latest data', and hovering any of these (plus New chat and the file actions) explains exactly what it does",
    ],
  },
  {
    version: "1.9.42",
    date: "2026-07-24",
    notes: [
      "Fixed the app briefly showing the wrong menu (e.g. an old 'Database Setup' item) right after clicking 'refresh for new version' — the account type is now retried on load and never falls back to a more-privileged view, so you always see the correct navigation",
      "Moving a file now shows a blocking indicator until it finishes, so you can't switch projects or pages mid-move (which could point the refresh at the wrong project)",
      "File lists now show the friendly original file name instead of the internal id prefix, and moving/reprocessing no longer stacks extra id prefixes onto the name",
    ],
  },
  {
    version: "1.9.41",
    date: "2026-07-24",
    notes: [
      "Fixed AI Insights returning an error for projects whose name starts with a number (e.g. '2ndRepository') — those projects can now be queried like any other",
      "After moving a file between projects, the destination project is now immediately ready for AI Insights questions (no first-question hiccup)",
    ],
  },
  {
    version: "1.9.40",
    date: "2026-07-23",
    notes: [
      "Cleaner upload result: the post-upload summary is now a compact, calm panel — a small success line, one clear 'rows imported' figure, and the rest of the metrics as tidy neutral pills — instead of the oversized multi-color stat cards",
      "File Upload now resets the upload area when you switch projects — a previous result (e.g. 'Duplicate Detected' or 'Upload complete') no longer lingers after changing the active project in the top selector",
      "Moving a file whose data already exists in the destination no longer silently removes it — Boomerang now tells you the data is already there and asks whether to remove it from this project or keep it",
    ],
  },
  {
    version: "1.9.39",
    date: "2026-07-23",
    notes: [
      "Groundwork for server-side file cleaning (rolling out gradually): when enabled, an upload sends only your original file and Boomerang cleans, standardizes, and de-duplicates it on the server — using the exact same engine as 'move between projects', so the two can never disagree",
      "Fixes Excel dates being read a day early: dates are now read exactly as stored in the spreadsheet (no timezone shift), and large ID numbers no longer turn into scientific notation",
    ],
  },
  {
    version: "1.9.38",
    date: "2026-07-22",
    notes: [
      "File Upload now shows which project you're uploading into, right at the top of the upload card",
      "Fixed moving a file into a project that already has the same data — it now recognizes the existing category and de-duplicates instead of creating a duplicate copy under a differently-named category",
    ],
  },
  {
    version: "1.9.37",
    date: "2026-07-22",
    notes: [
      "Fixed AI Insights showing a previous project's conversation after you archived the active project — it now reloads the correct project's history when the active project changes, even if it changed while you were on another page",
      "The Insight Engine panel now remembers whether you left it open or collapsed, and keeps that choice when you switch pages or reload (instead of always reopening)",
    ],
  },
  {
    version: "1.9.36",
    date: "2026-07-22",
    notes: [
      "File Upload: the drop area is now a compact bar instead of a large block, so your file list is visible without scrolling",
      "AI Insights: the Insight Engine panel is open by default, and when collapsed it now shows a clear labeled 'Insight Engine' tab on the right edge (instead of just an icon) so it's obvious how to bring it back",
      "New accounts' first project is now called 'Main Project' (clearer than the old 'My Data')",
    ],
  },
  {
    version: "1.9.35",
    date: "2026-07-22",
    notes: [
      "Sign-in screen now displays correctly on phones — the Boomerang brand lockup no longer overlaps the sign-in card, and its text is legible on the dark background (it moves above the card on small screens and stays in the top corner on desktop)",
    ],
  },
  {
    version: "1.9.34",
    date: "2026-07-21",
    notes: [
      "Manage Projects now has a colored header icon, and you can expand any project to see a read-only list of the files it contains (how many and which) — file actions still live in the File Upload page",
      "Creating a project now happens inside Manage Projects (a '+ New project' button there), so the left menu is a single clean 'Manage projects' entry styled like the other menu items",
      "Moving a file between projects now shows a working indicator while it re-cleans and de-duplicates, so you know it's in progress",
      "Fixed the moved file's report showing '0 columns detected' — the destination report now records the standardized column count correctly",
      "Fixed AI Insights loading the previous project's chat history (and File Upload briefly showing the previous project's files) right after switching projects — every project-scoped view now always reflects the project you just selected",
    ],
  },
  {
    version: "1.9.33",
    date: "2026-07-20",
    notes: [
      "Simpler projects — the Company layer is now hidden. You just work with Projects: no company field when creating one, no company grouping or company tab in Manage Projects, and the top bar shows just the active project. Each project gets its own color for quick recognition",
      "Move a file between projects — each file in File Upload now has a 'Move' action. Moving re-cleans and de-duplicates the file in the destination project and removes it from the current one (it re-classifies against the destination so column names line up). If a move is ever interrupted, the app detects and finishes/undoes it automatically",
    ],
  },
  {
    version: "1.9.32",
    date: "2026-07-20",
    notes: [
      "Leaner left menu — the 'Companies / Projects' section is now two clear rows (Manage projects, + New project) instead of a nested tree. Switching projects lives in the on-page 'Working in' bar (AI Insights & File Upload) and the top breadcrumb shows where you are, so the sidebar stays compact and unambiguous",
    ],
  },
  {
    version: "1.9.31",
    date: "2026-07-20",
    notes: [
      "Manage Projects now reads as a clean hierarchy — lightweight rows with a connecting guide line under each company (no more heavy boxes), and each project shows when it was last updated. The ⋮ menu and click-name-to-rename stay",
    ],
  },
  {
    version: "1.9.30",
    date: "2026-07-20",
    notes: [
      "Cleaner Manage Projects — each project row is now just its name, an Active badge, and a single ⋮ menu (Rename, Move, Archive, Delete) instead of four buttons. Click a project's name to rename it inline. Much easier to scan the company/project tree",
      "AI follow-up questions like 'the lowest one' now work — the assistant keeps enough of the previous answer to resolve items further down a ranked list",
    ],
  },
  {
    version: "1.9.29",
    date: "2026-07-20",
    notes: [
      "File Upload now has the same 'Working in' project bar as AI Insights, so you can see and switch the project you're uploading into right on the page (the sidebar stays in sync)",
      "Fixed chat memory not saving conversations in some cases — answered questions are now reliably remembered per project and restored when you return",
    ],
  },
  {
    version: "1.9.28",
    date: "2026-07-19",
    notes: [
      "File Upload now reloads its file list automatically when you switch projects (previously it kept showing the prior project's files until you refreshed)",
      "The Insight Engine panel now shows a clear 'Refreshing for this project…' indicator while it reloads suggestions after a project switch",
      "Made the 'Manage' control in the sidebar Projects section clearly visible (outlined, brand-colored) so it's easy to find",
    ],
  },
  {
    version: "1.9.27",
    date: "2026-07-19",
    notes: [
      "Fixed: switching projects in AI Insights now refreshes the suggested insights, anomaly alerts, and data-freshness for the newly selected project (previously they kept showing the prior project's until reload)",
    ],
  },
  {
    version: "1.9.26",
    date: "2026-07-19",
    notes: [
      "AI Insights now remembers each project's conversation — come back to a campaign and pick up right where you left off. It also gets smarter about that project over time. You can start a new chat, clear a project's history, and there's an on/off switch in Settings › Security",
      "Only your questions, the AI's answers, and chart definitions are saved (never the underlying rows), personal data in that text is masked, and each project keeps its own separate history",
      "When a project's data changes, the conversation shows a gentle 'data updated' note with a one-click 'Start new session' so old answers are never quietly mixed with new data",
      "Cleaner AI Insights header — the 'Working in' project switcher is the single control there (the top path no longer repeats it); every other page keeps the Company › Project breadcrumb",
      "The left menu now labels the section 'Companies / Projects' when you have more than one company, and each company name is tinted in its own color so they're easy to tell apart",
      "Fixed: renaming a company now updates its name everywhere immediately",
    ],
  },
  {
    version: "1.9.25",
    date: "2026-07-19",
    notes: [
      "Fixed the Projects list occasionally showing empty on load (a transient timing issue right after sign-in) — it now retries automatically, with a manual Retry if needed",
    ],
  },
  {
    version: "1.9.24",
    date: "2026-07-19",
    notes: [
      "Simpler, clearer projects — File Upload and AI Insights now show a 'Working in: <project>' bar right on the page, so you always see (and can switch) which project you're viewing, exactly where your files/answers are",
      "The Company layer stays out of your way until you actually have more than one company — most people just see a simple list of projects",
      "Creating a new project no longer drops you into an empty screen — you're told it's ready and can switch to it when you want to upload",
      "Companies & Projects read as a clear hierarchy (collapsible, like a file browser) once you do have multiple companies; the default company is labeled 'MyDefaultCompany'",
      "Clearer empty state — an empty project now explains its files are separate and how to switch projects",
    ],
  },
  {
    version: "1.9.23",
    date: "2026-07-19",
    notes: [
      "Clearer left menu — the Projects list is now its own labeled section (divided from the tools), each project is an obvious clickable row with a selection dot, and companies read as quiet group labels so the hierarchy is easy to scan",
      "Manage Projects is friendlier — every action is now a labeled button (Rename, Move, Archive, Delete) instead of mystery icons, projects show an ACTIVE badge, and the company is shown once as a group header (no more triple-listing)",
    ],
  },
  {
    version: "1.9.22",
    date: "2026-07-19",
    notes: [
      "Manage Projects is easier to navigate — projects are grouped by company with a search box to quickly find one",
      "The active project is now highlighted in a color pill in the top bar so it's obvious what you're working in at a glance",
    ],
  },
  {
    version: "1.9.21",
    date: "2026-07-19",
    notes: [
      "Projects & Companies — organize your data into projects (grouped by company) from a new Projects list in the left sidebar; each project keeps its files and AI Insights separate, and the active project is always shown in the top bar",
      "Create a project in one click with the new dialog (name + optional company); manage, rename, move between companies, archive, or delete projects — with an export reminder before any permanent delete",
      "AI Insights opens cleaner — the Insight Engine panel starts collapsed for a full-width answer area, and tucks away to a subtle button when hidden",
    ],
  },
  {
    version: "1.9.20",
    date: "2026-07-19",
    notes: [
      "No more 'stuck' sessions — if your sign-in expires while a page is open, Boomerang now silently refreshes it in the background so your data keeps loading; only if the session truly can't be renewed will it ask you to log in again (instead of showing blank panels on an unresponsive app)",
    ],
  },
  {
    version: "1.9.19",
    date: "2026-07-18",
    notes: [
      "Environment badge in the top bar so you always know which environment you're in — a prominent amber DEV badge in the development environment; a subtle green PROD badge for admins in production (regular users don't see it)",
      "Sign-out now returns to the marketing site only in production; in non-production environments it stays within the app",
    ],
  },
  {
    version: "1.9.18",
    date: "2026-07-18",
    notes: [
      "Admin → Usage: the deployed backend version is now shown in Settings, so backend-only releases (which don't change the app version) can be confirmed in the live environment",
    ],
  },
  {
    version: "1.9.17",
    date: "2026-07-14",
    notes: [
      "AI Insights: the right-hand Insight Engine panel can now be collapsed — click the arrow to hide it and give your answers, charts, and tables the full width; click the tab on the right edge to bring it back",
      "Admin: new Reset User Access tool — re-establish login for an existing user in one click; Boomerang automatically re-sends the invitation (if they never signed in) or starts a password reset (if they forgot their password)",
    ],
  },
  {
    version: "1.9.16",
    date: "2026-07-07",
    notes: [
      "The automatic sign-out after inactivity now happens at 2 hours (previously 30 minutes) — so long reporting sessions won't log you out mid-work; you'll still be signed out after 2 hours of no activity for security",
    ],
  },
  {
    version: "1.9.15",
    date: "2026-07-07",
    notes: [
      "AI Insights: new Export CSV button next to Export PDF — download the exact data behind any answer (chart, report table, or Source Records) as a spreadsheet-ready CSV",
      "The CSV contains the full result set (not just the first rows shown on screen) with raw values, so it opens cleanly in Excel or Google Sheets for your own analysis",
    ],
  },
  {
    version: "1.9.14",
    date: "2026-07-06",
    notes: [
      "The per-file cleaning report now shows the three professional data-cleaning phases — ① Data Profiling, ② Cleaning & Standardization, ③ Integrity Check — so you can see exactly what happened to your data at each stage",
      "The Integrity Check verdict (passed / review / blocked) and its reconciliation summary are now visible in the report, not just during upload",
      "Clearer labels: 'Phones/Emails Fixed' are now 'Phones/Emails Normalized' (we standardize formatting, e.g. trim spaces and lowercase emails — we don't alter your actual values)",
      "The post-upload summary now shows the same metrics as the detailed report (it was previously missing emails normalized and columns standardized)",
    ],
  },
  {
    version: "1.9.13",
    date: "2026-07-06",
    notes: [
      "New integrity/sanity check runs automatically at the end of each upload — it reconciles totals and row counts before vs after cleaning and flags anything worth a second look (e.g. a real number that got silently turned into 0, or a text column that unexpectedly lost data)",
      "Expected removals — deduplicated and incomplete/garbage rows — are accounted for but never counted as data distortion, so normal cleaning won't trigger a false alarm",
      "Applies to every file, not just financial ones: numeric columns get total-preservation checks, text columns get a population check, and every upload gets row-count reconciliation",
      "Warn-only by design: if something looks off you'll see a clear summary and can choose to upload anyway or stop and fix the source file — the only hard stop is when no valid rows survive cleaning",
    ],
  },
  {
    version: "1.9.12",
    date: "2026-07-05",
    notes: [
      "Privacy: when a file is uploaded, the small data sample used for automatic classification now has emails, phone numbers and long ID numbers masked before it's sent to the AI — the AI still recognizes the column types, but your actual contact details are not transmitted",
      "No change to how files are stored or to your reports; only the sample sent to the AI is masked",
    ],
  },
  {
    version: "1.9.11",
    date: "2026-07-05",
    notes: [
      "Security: tenant data isolation is now enforced at the AWS IAM level, not just in application code. Every AI Insights query runs under short-lived, per-tenant credentials whose access is restricted to that tenant's own storage — so even a code bug or prompt-injection attempt cannot reach another customer's data",
      "This runs automatically for every account; there is no change to how you upload files or ask questions",
    ],
  },
  {
    version: "1.9.10",
    date: "2026-07-04",
    notes: [
      "Security: the 30-minute inactivity logout now also triggers when the app is left open and the computer sleeps overnight — returning and clicking will now require you to sign in again, instead of silently resuming the session",
      "Idle time is re-checked on every interaction, when the tab regains focus, and on a periodic backstop, so the session can't be kept alive just by leaving the tab open",
    ],
  },
  {
    version: "1.9.9",
    date: "2026-07-04",
    notes: [
      "AI Insights now follows explicit calculation notes in your question exactly — e.g. lower-inclusive time/price bands are computed correctly on the first try (a 3:30:00 finish falls in the 3:30-4:00 band, not 3:00-3:30)",
      "More accurate math: numbers stored with $ or commas are parsed correctly, percentages guard against divide-by-zero, medians use true percentiles, and category grouping ignores case/spacing differences",
      "Business metrics (ROAS, CAC, LTV, AOV, margin, churn) now state the exact formula used so you can verify the calculation",
    ],
  },
  {
    version: "1.9.8",
    date: "2026-07-03",
    notes: [
      "Charts no longer cram metrics of very different sizes onto one axis (e.g. revenue vs margin vs job count) — the smaller metrics used to become invisible bars with overlapping labels; now only comparable-scale metrics share a chart",
      "Value labels on grouped multi-metric bars no longer overlap — the details stay available in the tooltip, legend, and table",
    ],
  },
  {
    version: "1.9.7",
    date: "2026-07-03",
    notes: [
      "Security: the 30-minute inactivity logout now also applies across browser refreshes and restarts — an idle gap (e.g. overnight) forces you to sign in again instead of restoring the session automatically",
      "Your last-activity time is remembered so the idle window is enforced the moment you return, not just while the tab stays open",
      "Admin Usage: the Per-customer list is now ordered by most recently active first, so Active users appear at the top and never-active accounts sink to the bottom",
    ],
  },
  {
    version: "1.9.6",
    date: "2026-06-27",
    notes: [
      "File metrics panel no longer hangs on \"Loading…\" — it now clearly says when a file has no processing report yet",
      "Reprocess now self-heals files that were missing a processing record, so it can regenerate metrics for older/re-homed files",
    ],
  },
  {
    version: "1.9.5",
    date: "2026-06-27",
    notes: [
      "Fixed: \"per year\" / time-series questions (e.g. total revenue per year) now generate a chart — previously no graph appeared because the period column was treated as data, not an axis",
      "Charts are more reliable — values with $ or commas, and columns with a blank first row, no longer get skipped",
      "Charts can now plot multiple measures at once (e.g. revenue and cost per year)",
      "Time-based charts order chronologically and money values use currency formatting",
    ],
  },
  {
    version: "1.9.4",
    date: "2026-06-27",
    notes: [
      "Files are now always cleaned, normalized, and deduplicated — even when auto-classification can't name the type (previously such files could be stored raw)",
      "New per-file Quality Control verdict (PASS / PARTIAL) in each file's report — shows whether it was classified, cleaned, normalized, and deduplicated",
      "Upload now warns you before finishing if a file didn't fully pass the quality checks, so nothing slips through unnoticed",
      "New \"Reprocess\" action on each file — re-clean from the original and assign a proper category (promotes a PARTIAL file to PASS) without re-uploading",
      "Admin: new Data Health check flags any data that drifted (file with no schema, schema with no files, or files left in the general_data fallback)",
    ],
  },
  {
    version: "1.9.3",
    date: "2026-06-26",
    notes: [
      "Admin: new Models tab to trial a candidate Claude model on one tenant before switching everyone, with a built-in step-by-step checklist (LLM model-switch readiness)",
    ],
  },
  {
    version: "1.9.2",
    date: "2026-06-26",
    notes: [
      "Admin Usage: customer Status now reflects real-time presence (Active / Idle / Offline) based on recent activity, instead of always showing \"Active\"",
      "The app now sends a lightweight heartbeat while open so presence stays accurate",
    ],
  },
  {
    version: "1.9.1",
    date: "2026-06-26",
    notes: [
      "Time-based charts now plot points in chronological order (fixes dates showing out of sequence on the x-axis)",
    ],
  },
  {
    version: "1.9.0",
    date: "2026-06-26",
    notes: [
      "More reliable answers — fixed a case where archiving all files in a category could leave a stale table behind, causing some questions to error",
      "Restoring a file now rebuilds its data schema automatically when needed",
    ],
  },
  {
    version: "1.8.5",
    date: "2026-06-26",
    notes: [
      "New-version notifications — you'll see a banner to reload when an update is available",
      "More prominent version badge in the sidebar",
      "Charts and tables color gains green and losses red for at-a-glance reading",
    ],
  },
  {
    version: "1.8.4",
    date: "2026-06-26",
    notes: [
      "Detailed lists now say \"showing the first 100\" instead of implying that's the total — the count and the detail view no longer look mismatched",
      "Admin pages retry through brief server wake-ups instead of getting stuck loading",
    ],
  },
  {
    version: "1.8.3",
    date: "2026-06-25",
    notes: [
      "Smarter answers when a filter finds nothing — Boomerang now matches the real values in your data (e.g. \"Angie's List\" → \"Angi\") instead of returning an empty result",
      "Works for totals too (e.g. counts that came back as 0 due to a value mismatch)",
    ],
  },
  {
    version: "1.8.2",
    date: "2026-06-25",
    notes: [
      "Charts now show friendly dates on the axis and tooltips (e.g. \"Dec 29, 2025\")",
      "Clearer \"no results\" answers: when a date range has no data, Boomerang now tells you the date range your data actually covers",
    ],
  },
  {
    version: "1.8.1",
    date: "2026-06-25",
    notes: [
      "Reports & tables now show friendly dates (e.g. \"Dec 29, 2025\" instead of \"2025-12-29 00:00:00.000\")",
      "Revenue and other money columns now display with a $ and thousands separators",
    ],
  },
  {
    version: "1.8.0",
    date: "2026-06-25",
    notes: [
      "More reliable connections — questions and uploads now auto-retry through brief network hiccups instead of showing a 'Failed to fetch' error",
      "Smarter answers — text-value filters now match regardless of capitalization or extra spaces, reducing accidental 'no results'",
      "Admin: new Feature Flags panel to roll out new capabilities gradually (global or per-customer)",
    ],
  },
  {
    version: "1.7.0",
    date: "2026-06-23",
    notes: [
      "Security: optional multi-factor authentication (MFA) — enable an authenticator app from the new Security page",
      "First-login Terms & Disclaimer acceptance (recorded for your account)",
      "UI refresh — leaner, more modern app shell, File Upload, and AI Insights screens",
      "Skeleton loaders on the file list and suggested-insights panel for a faster feel",
      "Per-user data isolation — each user's uploads stay private, even within the same company",
    ],
  },
  {
    version: "1.6.0",
    date: "2026-06-16",
    notes: [
      "Async query handling — complex cross-table queries no longer time out",
      "Fixed UNION ALL queries being truncated (cross-business-line analysis now works)",
      "Follow-up questions reuse the previous query (e.g., 'show that in a table')",
      "Smarter 'no results' — retries against other tables before giving up",
    ],
  },
  {
    version: "1.5.0",
    date: "2026-06-15",
    notes: [
      "Table-format reports — ask for a 'report' or 'table' to see full data inline",
      "PDF export now includes the full data table (multi-page, selectable text)",
      "Enhanced charts: smart aggregation, currency formatting, area/donut/stacked types",
    ],
  },
  {
    version: "1.4.0",
    date: "2026-06-15",
    notes: [
      "Removed NAT Gateway — 87% lower infrastructure cost",
      "Lambda memory bump (2x CPU) for faster AI responses",
      "Session expiry modal after 30 min inactivity",
    ],
  },
  {
    version: "1.3.0",
    date: "2026-06-12",
    notes: [
      "AI budget cap & rate limiting with real token-based cost tracking",
      "Conversation history — follow-up questions now have context",
      "Streaming AI responses (typed word-by-word)",
      "User feedback widget (report bugs / request features)",
    ],
  },
  {
    version: "1.2.0",
    date: "2026-06-12",
    notes: [
      "AI-driven data normalizers (replaces static keyword matching)",
      "Excel binary detection (handles .csv files that are actually Excel)",
      "Server-side re-processing on file restore (recovers all valid rows)",
    ],
  },
  {
    version: "1.1.0",
    date: "2026-06-11",
    notes: [
      "AI Insights powered by Claude Sonnet 4.5",
      "Cross-file deduplication & data cleaning pipeline",
      "Anomaly detection alerts",
    ],
  },
  {
    version: "1.0.0",
    date: "2026-06-10",
    notes: ["Initial POC release — file upload, AI classification, multi-tenant"],
  },
];
